// Modules
import React from "react";
// @ts-ignore
import { LocalizationContext } from "react-localize";

// Styles
import "./MvSortMobileTable.sass";

// Components
import MvLoader from "Generic/MvLoader/MvLoader";
import MvIcon from "Generic/MvIcon";

// Constants
import {RequestStatus} from "Constants";

// Utils
import {SortDirection} from "Utils/SortContracts";

export type TableHeaders = {
  text: string;
  sortable: boolean;
  curSortDirection?: SortDirection;
  onSort?(): void;
  activeSort?: boolean;
};

export interface TableRow {
  rowKey: string;
  rowCells: {[key: string]: any};
  rowExpanded?: React.ReactNode;
}

export type Props = {
  rowKey: string;
  headers: TableHeaders[];
  rows: TableRow[];
  dataStatus: RequestStatus;
  headerSection: React.ReactNode;
  rowsExpandable?: boolean;
  tableExpandable?: boolean;
  minWidth: number;
  columnStyles?: any;
  tableExpanded?: boolean;
};

interface ExpandedRow {
  rowKey: string;
  expanded: boolean;
}

type State = {
  screenWidth: number;
  tableExpanded: boolean;
  expandedRows: {[key: string]: boolean};
  allRowsCollapsed: boolean;
};

class MvSortMobileTable extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      screenWidth: 0,
      tableExpanded: props.tableExpanded ? props.tableExpanded : false,
      expandedRows: {},
      allRowsCollapsed: true,
    };
  }

  componentDidMount() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  /**
   * fired when the browser is resized
   */
  handleResize = () => {
    this.setState({ screenWidth: window.innerWidth });
  }

  /**
   * fired when the expand table button is clicked
   */
  toggleTable = () => {
    const tableExpanded = !this.state.tableExpanded;
    this.setState({ tableExpanded });
  }

  /**
   * fired when the expand icon for all rows (top left) is clicked
   */
  toggleAllRows = () => {
    // if all rows are collapsed, then open all (set to true), else collapse all
    // ie. if true, set all to expand: true
    const shouldExpand = this.allRowsCollapsed(this.state.expandedRows);
    const expandedRows: {[key: string]: boolean} = {};

    this.props.rows.forEach((row) => {
     expandedRows[row.rowKey] = shouldExpand;
    });

    this.setState({ expandedRows, allRowsCollapsed: !shouldExpand });
  }

  /**
   * check if all rows are collapsed
   */
  allRowsCollapsed = (rows: {[key: string]: boolean}):boolean => {
    return !Object.values(rows).some(expanded => expanded);
  }

  /**
   * fired when a row's expand icon is clicked
   */
  toggleExpandRow = ({rowKey, expanded}: ExpandedRow) => {
    const expandedRows = {...this.state.expandedRows};

    // set the row's expanded state
    expandedRows[rowKey] = expanded;

    const allRowsCollapsed = this.allRowsCollapsed(expandedRows);

    this.setState({
      expandedRows,
      allRowsCollapsed
    });
  }

  render() {
    const { localize } = this.context;

    const {
      rowKey,
      headers,
      rows,
      headerSection,
      rowsExpandable,
      tableExpandable,
      minWidth,
      dataStatus,
      columnStyles,
    } = this.props;

    const {
      tableExpanded,
      screenWidth,
      expandedRows,
      allRowsCollapsed,
    } = this.state;

    const isLoading = dataStatus === RequestStatus.LOADING;

    const showTable = tableExpanded || !tableExpandable;
    const numberText = [ "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen", "twenty", ];

    return (
      <section className={`mv-sort-table ${showTable ? "table-show" : "table-hide"}`}>
        {(tableExpandable || headerSection) &&
          <div className="table-header-section">
            {tableExpandable &&
              <button className="expand-icon" onClick={this.toggleTable}>
                <MvIcon
                  color="#ffffff"
                  style={{
                    height: "14px",
                    width: "14px"
                  }}
                  icon={tableExpanded ? "minus" : "plus"}
                />
              </button>
            }
            {headerSection}
          </div>
        }

        {showTable &&
          <div className="scroll-outer">  {/* put overflow in the sass file */}
            <div className="scroll-inner" style={{minWidth}}>
              <div className="mv-table">
                <div className="table-row col-headers">
                  {rowsExpandable === true &&
                    <div className="table-header expand-icon-cell">
                      <button
                        className="expand-icon"
                        onClick={this.toggleAllRows}
                      >
                        <MvIcon
                          color="#ffffff"
                          style={{
                            height: "14px",
                            width: "14px"
                          }}
                          icon={allRowsCollapsed ? "plus" : "minus"}
                        />
                      </button>
                    </div>
                  }
                  {headers.map((header, colIdx) => {
                    const headerStyle = (columnStyles &&
                      columnStyles[colIdx] !== undefined) ?
                      columnStyles[colIdx] : {};
                    const divColNumClassName = numberText[colIdx];

                    return (
                      <div
                      className={`table-header ${divColNumClassName}`}
                      key={header.text}
                      style={headerStyle}
                      >
                        {header.text}
                        {header.sortable &&
                          <span
                            className="sort-icon"
                            onClick={header.onSort}
                          >
                            <MvIcon
                              color={header.activeSort || header.activeSort === undefined ? "#009587" : "black"}
                              style={{
                                height: "14px",
                                width: "14px"
                              }}
                              icon={
                                header.curSortDirection === SortDirection.ASC ?
                                  "sortDown" : "sortUp"
                              }
                            />
                          </span>
                        }
                      </div>
                    );
                  })}
                </div>
                <div className={`loader-container ${isLoading ? "loading" : ""}`}>
                  <MvLoader show={isLoading}>
                    <div className="table-rows">
                      {rows.map((row) => {

                        const rowExpanded = expandedRows[row.rowKey] === true;

                        return (
                          <div
                            className="table-row"
                            key={row.rowKey}
                          >
                            {rowsExpandable === true &&
                              <div className="table-data expand-icon-cell">
                                <button
                                  className="expand-icon"
                                  onClick={() => this.toggleExpandRow({
                                    rowKey: row.rowKey,
                                    expanded: !rowExpanded,
                                  })}
                                >
                                  <MvIcon
                                    color="#ffffff"
                                    style={{
                                      height: "14px",
                                      width: "14px"
                                    }}
                                    icon={rowExpanded ? "minus" : "plus"}
                                  />
                                </button>
                              </div>
                            }
                            <div style={{paddingBottom: '1rem'}}>
                              <div className="villas-mobile-table-row">
                                {row.rowCells['room']}
                              </div>
                              {Object.keys(row.rowCells).map((cellDataKey, cellIndex) => {
                                if (cellDataKey === 'room') return;
                                const cellStyle = (columnStyles &&
                                columnStyles[cellIndex] !== undefined) ?
                                  columnStyles[cellIndex] : {};
                                const divColNumClassName = numberText[cellIndex];

                                return (
                                  <div
                                    key={`${row.rowKey}-${cellIndex}`}
                                    style={{
                                      paddingLeft: '26px',
                                    }}
                                  >
                                    <b>{cellDataKey==="views"?"View":cellDataKey==="points"?"Total points Needed":cellDataKey}</b>: {row.rowCells[cellDataKey]}
                                  </div>
                                );
                              })}
                              {rowsExpandable &&
                                <div
                                  className={`row-expanded-container ${rowExpanded ? 'expanded' : ''}`}
                                >
                                  {row.rowExpanded}
                                </div>
                              }
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </MvLoader>
                </div>
              </div>
            </div>
          </div>
        }
      </section>
    );
  }
}

MvSortMobileTable.contextType = LocalizationContext;
export {
  MvSortMobileTable,
};
